import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Check, ArrowRight, ArrowUpRight, MapPin, Phone, Mail, Clock, Building2,
  FileText, Download, AlertTriangle, ShieldAlert, Sparkles,
  ChevronDown, ChevronUp, Plus, Minus, Eye,
} from "lucide-react";
import { Button } from "../ui/button";
import FeaturedProducts from "../FeaturedProducts";
import AgentApplicationForm from "../AgentApplicationForm";
import PaymentReferenceForm from "../PaymentReferenceForm";
import StripeBuyButtonBlock from "../StripeBuyButtonBlock";
import { countryCodeToFlag } from "../../utils/countryFlag";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Release 1.C — Premium SaaS / logistics-tech rendering of CMS blocks.
 * All blocks accept the same props as before. Visual upgrade only — no
 * functional or structural changes that could break protect_critical_blocks
 * or impacted page flows.
 */
function tryParseJSON(s) {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { return JSON.parse(s); } catch { return []; }
}

// ===================== HERO =====================
const OVERLAY_STYLES = {
  none: "",
  blue_gradient: "bg-gradient-to-br from-brand-900/90 via-brand-700/75 to-brand-500/55",
  dark_gradient: "bg-gradient-to-br from-brand-900/90 via-brand-700/75 to-brand-500/55",
};

const OBJECT_POSITIONS = {
  center: "object-center",
  top: "object-top",
  bottom: "object-bottom",
  left: "object-left",
  right: "object-right",
};

// Resolve media URLs — `/api/media/{id}` shapes need the backend host
// prefix when served from the React preview/production domain.
function resolveImageUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}

export function HeroBlock({ data }) {
  if (!data) return null;
  const img = resolveImageUrl(data.image_url);
  const hasImage = !!img;
  const overlayClass = hasImage
    ? OVERLAY_STYLES[data.overlay_style] || OVERLAY_STYLES.blue_gradient
    : "";
  const positionClass = OBJECT_POSITIONS[data.image_position] || OBJECT_POSITIONS.center;
  return (
    <section
      className={`relative overflow-hidden ${hasImage ? "text-white" : "hero-mesh text-brand-900"}`}
      data-testid="cms-block-hero"
    >
      {hasImage && (
        <div className="absolute inset-0">
          <img
            src={img}
            alt={data.image_alt || data.title || ""}
            title={data.image_title || undefined}
            className={`w-full h-full object-cover ${positionClass}`}
            loading="eager"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          {data.overlay_style !== "none" && (
            <div className={`absolute inset-0 ${overlayClass}`} />
          )}
          <div className="absolute inset-0 grid-pattern-light opacity-30" aria-hidden="true" />
        </div>
      )}
      {!hasImage && <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" aria-hidden="true" />}

      <div className="relative container-x py-20 sm:py-24 lg:py-28">
        <div className="max-w-3xl">
          {data.eyebrow && (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] tracking-[0.18em] uppercase font-chivo font-bold mb-5 ${
              hasImage ? "bg-white/15 text-white border border-white/20 backdrop-blur-md" : "bg-brand-50 text-brand-500 border border-brand-100"
            }`}>
              <Sparkles className="w-3 h-3" strokeWidth={2} />
              {data.eyebrow}
            </span>
          )}
          {data.title && (
            <h1 className={`font-chivo font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] ${hasImage ? "text-white" : "text-brand-900"}`}>
              {data.title}
              {data.subtitle && (
                <span className={`block mt-2 ${hasImage ? "text-brand-200" : "text-brand-500"}`}>
                  {data.subtitle}
                </span>
              )}
            </h1>
          )}
          {data.text && (
            <p className={`mt-6 text-lg leading-relaxed max-w-2xl ${hasImage ? "text-white/85" : "text-ink-500"}`}>
              {data.text}
            </p>
          )}
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            {data.button_1_text && data.button_1_link && (
              <Link
                to={data.button_1_link}
                className={hasImage
                  ? "inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-white text-brand-900 hover:bg-brand-50 font-chivo font-bold text-[14px] tracking-wide uppercase shadow-brand-card transition-all active:scale-[0.98]"
                  : "btn-primary-lg"}
                data-testid="cms-hero-cta-1"
              >
                {data.button_1_text}
                <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
              </Link>
            )}
            {data.button_2_text && data.button_2_link && (
              <Link
                to={data.button_2_link}
                className={hasImage ? "btn-outline-light" : "btn-secondary"}
                data-testid="cms-hero-cta-2"
              >
                {data.button_2_text}
              </Link>
            )}
          </div>

          {/* Trust indicators (optional) */}
          {Array.isArray(data.trust_items) && data.trust_items.length > 0 && (
            <div className={`mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm ${hasImage ? "text-white/80" : "text-ink-500"}`}>
              {data.trust_items.map((it, i) => (
                <span key={i} className="inline-flex items-center gap-2">
                  <Check className={`w-4 h-4 ${hasImage ? "text-brand-200" : "text-brand-500"}`} strokeWidth={2.25} />
                  {it}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ===================== TEXT SECTION =====================
function _TextSectionInner({ data, compact }) {
  const paragraph = data.text || data.body;
  const bullets = Array.isArray(data.bullets) ? data.bullets.filter(Boolean) : [];
  const warning = data.warning_text;
  const email = data.email;
  return (
    <>
      {data.title && (
        <h2 className={`font-chivo font-black ${compact ? "text-xl sm:text-2xl" : "text-3xl sm:text-4xl"} text-brand-900 tracking-tight`}>
          {data.title}
        </h2>
      )}
      {paragraph && (
        <p className={`${compact ? "mt-3 text-[14.5px]" : "mt-5 text-[16px]"} text-ink-500 leading-relaxed whitespace-pre-wrap`}>{paragraph}</p>
      )}
      {bullets.length > 0 && (
        <ul className={`${compact ? "mt-4" : "mt-6"} space-y-3`} data-testid="cms-text-bullets">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-[14.5px] text-ink-700">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-500/10 text-brand-500 flex items-center justify-center mt-0.5">
                <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
              <span className="leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      )}
      {warning && (
        <div
          className={`${compact ? "mt-5" : "mt-7"} rounded-xl border-l-4 border-amber-500 bg-amber-50 px-5 py-4 text-sm text-amber-900 font-chivo font-semibold flex items-start gap-3`}
          data-testid="cms-text-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{warning}</span>
        </div>
      )}
      {email && (
        <p className={`${compact ? "mt-5" : "mt-7"} text-sm text-ink-500`}>
          <a
            href={`mailto:${email}`}
            data-testid="cms-text-email-link"
            className="link-brand"
          >
            {email}
          </a>
        </p>
      )}
      {data.button_text && data.button_link && (
        <div className={compact ? "mt-5" : "mt-8"}>
          <Link to={data.button_link} className={compact ? "btn-secondary" : "btn-dark"}>
            {data.button_text}
            <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
          </Link>
        </div>
      )}
    </>
  );
}

export function TextSection({ data }) {
  if (!data || (!data.title && !data.text && !data.body && !(data.bullets && data.bullets.length))) return null;
  return (
    <section className="py-20 bg-white" data-testid="cms-block-text-section">
      <div className="container-narrow">
        <_TextSectionInner data={data} compact={false} />
      </div>
    </section>
  );
}

// Compact variant used inside auto-paired 2-column grids (e.g. /online-betalen)
export function TextSectionCard({ data }) {
  if (!data || (!data.title && !data.text && !data.body && !(data.bullets && data.bullets.length))) return null;
  return (
    <div className="card-premium p-6 sm:p-7 h-full" data-testid="cms-block-text-section">
      <_TextSectionInner data={data} compact={true} />
    </div>
  );
}

// ===================== SERVICE CARDS =====================
export function ServiceCardsBlock({ data }) {
  if (!data) return null;
  const cards = tryParseJSON(data.cards_json);
  if (cards.length === 0) return null;
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="cms-block-service-cards">
      <div className="container-x">
        {data.subtitle && (
          <span className="eyebrow">{data.subtitle}</span>
        )}
        {data.title && (
          <h2 className="font-chivo font-black text-3xl sm:text-4xl lg:text-5xl text-brand-900 tracking-tight mt-3 max-w-3xl">
            {data.title}
          </h2>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {cards.map((c, i) => (
            <div
              key={i}
              className="card-premium p-7 group relative overflow-hidden"
              data-testid={`cms-service-card-${i}`}
            >
              {/* Decorative gradient corner */}
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-brand-500/5 group-hover:bg-brand-500/10 transition-colors blur-xl pointer-events-none" />
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-500/10 text-brand-500 group-hover:bg-brand-500 group-hover:text-white transition-all">
                <Check className="w-5 h-5" strokeWidth={2.25} />
              </span>
              <h3 className="mt-5 font-chivo font-bold text-lg text-brand-900">{c.title}</h3>
              <p className="text-[14.5px] text-ink-500 mt-2.5 leading-relaxed">{c.desc || c.description}</p>
              {c.link && (
                <Link to={c.link} className="inline-flex items-center gap-1 mt-5 text-sm font-chivo font-bold text-brand-500 hover:text-brand-600 transition-colors">
                  Lees meer <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===================== FAQ =====================
function FaqItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-premium overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-slate-50/50 transition-colors"
      >
        <span className="font-chivo font-bold text-[15px] text-brand-900">{q}</span>
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-500/10 text-brand-500 flex items-center justify-center">
          {open ? <Minus className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6 -mt-1 text-[14.5px] text-ink-500 leading-relaxed whitespace-pre-wrap border-t border-slate-100">
          <div className="pt-4">{a}</div>
        </div>
      )}
    </div>
  );
}

export function FaqGroup({ blocks }) {
  if (!blocks || blocks.length === 0) return null;
  const items = blocks
    .slice()
    .sort((a, b) => (a.data?.sort_order || 0) - (b.data?.sort_order || 0));
  return (
    <section className="py-24 bg-white" data-testid="cms-block-faq-group">
      <div className="container-narrow">
        <span className="eyebrow">FAQ</span>
        <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-3 mb-10">
          Veelgestelde vragen
        </h2>
        <div className="space-y-3">
          {items.map((b, i) => (
            <FaqItem key={b.id} q={b.data?.question} a={b.data?.answer} testid={`cms-faq-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ===================== CTA BANNER =====================
export function CtaBannerBlock({ data }) {
  if (!data || (!data.title && !data.text)) return null;
  // Phase 18.16-B fix Feb 2026 — external URLs (e.g. ManagerCargo
  // registratie) must open in a new tab via <a target="_blank">
  // instead of react-router <Link> (which silently breaks on
  // absolute URLs and never adds rel="noopener noreferrer").
  const href = data.button_link || "";
  const isExternal = /^(https?:|mailto:|tel:)/i.test(href);
  const btnClassName = "inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white text-brand-900 hover:bg-brand-50 font-chivo font-bold text-[14px] tracking-wide uppercase shadow-brand-card transition-all active:scale-[0.98]";
  return (
    <section className="py-20 relative overflow-hidden" data-testid="cms-block-cta-banner">
      <div className="container-x">
        <div className="relative rounded-3xl bg-brand-gradient text-white p-10 lg:p-14 shadow-brand-deep overflow-hidden">
          <div className="absolute inset-0 grid-pattern-light opacity-50 pointer-events-none" aria-hidden="true" />
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-500/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-12 w-72 h-72 rounded-full bg-brand-300/20 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              {data.title && (
                <h2 className="font-chivo font-black text-3xl sm:text-4xl tracking-tight leading-tight">{data.title}</h2>
              )}
              {data.text && <p className="mt-3 text-white/85 text-[15px] leading-relaxed">{data.text}</p>}
            </div>
            {data.button_text && href && (
              isExternal ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={btnClassName}
                  data-testid="cms-cta-button"
                >
                  {data.button_text} <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
                </a>
              ) : (
                <Link
                  to={href}
                  className={btnClassName}
                  data-testid="cms-cta-button"
                >
                  {data.button_text} <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===================== PRICING =====================
export function PricingTableBlock({ tariffs }) {
  if (!tariffs || tariffs.length === 0) return null;
  return (
    <section className="py-24 bg-gradient-to-b from-white via-slate-50 to-white" data-testid="cms-block-pricing" id="tarieven">
      <div className="container-narrow">
        <span className="eyebrow">Tarieven</span>
        <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-3 mb-10">
          Transparante tarieven
        </h2>
        <div className="card-premium overflow-hidden divide-y divide-slate-100">
          {tariffs.map((t) => {
            const hasOptions = !!t.has_options && Array.isArray(t.options) && t.options.length > 0;
            if (hasOptions) {
              return (
                <div key={t.id} className="p-6" data-testid={`cms-tariff-grouped-${t.id}`}>
                  <h3 className="font-chivo font-bold text-base text-brand-900">{t.title}</h3>
                  {t.description && <p className="text-sm text-ink-500 mt-1.5 leading-relaxed">{t.description}</p>}
                  <div className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-100 bg-slate-50/40">
                    {t.options
                      .filter((o) => o.active !== false)
                      .slice()
                      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                      .map((o) => {
                        const ptype = o.price_type || "fixed";
                        let oLabel;
                        if (ptype === "on_request") {
                          oLabel = <span className="font-chivo font-black text-sm text-brand-500">Prijs op aanvraag</span>;
                        } else if (ptype === "from_price") {
                          oLabel = (
                            <span className="font-chivo font-black text-base text-brand-900 font-mono-num">
                              <span className="text-[10px] text-ink-400 font-bold uppercase tracking-wide mr-1">Vanaf</span>
                              {o.currency || t.currency || "EUR"} {Number(o.price ?? 0).toFixed(2)}
                            </span>
                          );
                        } else {
                          oLabel = (
                            <span className="font-chivo font-black text-base text-brand-900 font-mono-num">
                              {o.currency || t.currency || "EUR"} {Number(o.price ?? 0).toFixed(2)}
                            </span>
                          );
                        }
                        return (
                          <div key={o.id} className="px-4 py-3 flex flex-wrap items-baseline justify-between gap-2" data-testid={`cms-tariff-option-${o.id}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-chivo font-bold text-brand-900">{o.option_name}</div>
                              {o.description && <div className="text-xs text-ink-500 mt-0.5">{o.description}</div>}
                            </div>
                            <div className="text-right">
                              {oLabel}
                              {o.unit && ptype !== "on_request" && (
                                <div className="text-[10px] text-ink-400">per {o.unit}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {t.note && <p className="text-xs text-ink-400 mt-3">{t.note}</p>}
                </div>
              );
            }
            const priceType = t.price_type || "fixed";
            let priceLabel;
            if (priceType === "on_request") {
              priceLabel = (
                <div className="font-chivo font-black text-base text-brand-500" data-testid={`cms-tariff-price-on-request-${t.id}`}>
                  Prijs op aanvraag
                </div>
              );
            } else if (priceType === "from_price") {
              priceLabel = (
                <div className="font-chivo font-black text-2xl text-brand-900 font-mono-num" data-testid={`cms-tariff-price-from-${t.id}`}>
                  <span className="text-xs text-ink-400 font-bold uppercase tracking-wide mr-1">Vanaf</span>
                  {t.currency || "EUR"} {Number(t.price).toFixed(2)}
                </div>
              );
            } else {
              priceLabel = (
                <div className="font-chivo font-black text-2xl text-brand-900 font-mono-num" data-testid={`cms-tariff-price-fixed-${t.id}`}>
                  {t.currency || "EUR"} {Number(t.price).toFixed(2)}
                </div>
              );
            }
            return (
              <div key={t.id} className="p-6 flex flex-wrap items-baseline justify-between gap-4 hover:bg-slate-50/40 transition-colors" data-testid={`cms-tariff-${t.id}`}>
                <div className="min-w-0">
                  <h3 className="font-chivo font-bold text-base text-brand-900">{t.title}</h3>
                  {t.description && <p className="text-sm text-ink-500 mt-1.5 leading-relaxed">{t.description}</p>}
                  {t.note && <p className="text-xs text-ink-400 mt-1.5">{t.note}</p>}
                </div>
                <div className="text-right">
                  {priceLabel}
                  {t.line && priceType !== "on_request" && (
                    <div className="text-xs text-ink-400">
                      per {t.line} {t.vat_included ? "(incl. btw)" : "(excl. btw)"}
                    </div>
                  )}
                  {t.line && priceType === "on_request" && (
                    <div className="text-xs text-ink-400">{t.line}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===================== LOCATION CARDS =====================
export function LocationCardsBlock({ data }) {
  const [hubs, setHubs] = useState([]);
  useEffect(() => {
    if ((data?.source || "db") !== "db") return;
    axios.get(`${BACKEND_URL}/api/website/locations`).then((r) => {
      setHubs(Array.isArray(r.data) ? r.data.filter((h) => h.active !== false) : []);
    }).catch(() => setHubs([]));
  }, [data?.source]);
  if (!data) return null;
  const showHubs = hubs.slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="cms-block-location-cards">
      <div className="container-x">
        {data.title && (
          <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight">
            {data.title}
          </h2>
        )}
        {data.subtitle && <p className="text-ink-500 mt-3 max-w-2xl text-[15px] leading-relaxed">{data.subtitle}</p>}
        {showHubs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="cms-location-grid">
            {showHubs.map((h) => {
              const flag = countryCodeToFlag(h.country_code);
              return (
              <div key={h.id} className="card-premium p-6 group" data-testid={`cms-location-${h.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  {/* Phase 18.17 fix Feb 2026 — show country flag if a
                      country_code is available, fall back to the legacy
                      Building2 icon otherwise (e.g. a freshly seeded hub
                      where the admin hasn't filled in the ISO-2 code
                      yet). */}
                  {flag ? (
                    <span
                      className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-xl leading-none overflow-hidden"
                      aria-hidden="true"
                      data-testid={`cms-location-flag-${(h.country_code || "").toLowerCase()}`}
                    >
                      {flag}
                    </span>
                  ) : (
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-500/10 text-brand-500 group-hover:bg-brand-500 group-hover:text-white transition-colors flex items-center justify-center">
                      <Building2 className="w-4 h-4" strokeWidth={1.75} />
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-[0.18em] font-chivo font-bold text-brand-500">{h.country}</span>
                </div>
                <h3 className="font-chivo font-black text-lg text-brand-900">{h.hub_label}</h3>
                <div className="mt-4 space-y-2 text-sm text-ink-500">
                  {h.address && <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brand-500" strokeWidth={1.75} /><span>{h.address}</span></div>}
                  {h.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-brand-500" strokeWidth={1.75} /><span>{h.phone}</span></div>}
                  {h.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-brand-500" strokeWidth={1.75} /><span className="break-all">{h.email}</span></div>}
                  {h.opening_hours && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-brand-500" strokeWidth={1.75} /><span>{h.opening_hours}</span></div>}
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-ink-400 mt-6">Geen locaties beschikbaar.</p>
        )}
      </div>
    </section>
  );
}

// ===================== PRODUCT HIGHLIGHTS =====================
export function ProductHighlightsBlock({ data }) {
  if (!data) return null;
  return (
    <FeaturedProducts
      limit={data.limit || 3}
      title={data.title}
      subtitle={data.subtitle}
      ctaText={data.cta_text}
      ctaLink={data.cta_link}
      selection={data.selection || "featured"}
    />
  );
}

// ===================== CONTACT BLOCK =====================
export function ContactBlock({ data }) {
  if (!data) return null;
  return (
    <section className="py-20 bg-white" data-testid="cms-block-contact">
      <div className="container-narrow grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="card-premium p-8">
          {data.company_name && <h2 className="font-chivo font-black text-2xl text-brand-900 mb-5">{data.company_name}</h2>}
          <ul className="space-y-4 text-[15px] text-ink-700">
            {data.address && <li className="flex items-start gap-3" data-testid="cms-contact-address"><MapPin className="w-4.5 h-4.5 mt-0.5 text-brand-500" strokeWidth={1.75} /><span>{data.address}</span></li>}
            {data.phone && <li className="flex items-center gap-3" data-testid="cms-contact-phone"><Phone className="w-4.5 h-4.5 text-brand-500" strokeWidth={1.75} /><a href={`tel:${data.phone}`} className="hover:text-brand-500 transition-colors">{data.phone}</a></li>}
            {data.whatsapp && <li className="flex items-center gap-3"><Phone className="w-4.5 h-4.5 text-[#25D366]" strokeWidth={1.75} /><a href={`https://wa.me/${data.whatsapp.replace(/\D/g,"")}`} className="hover:text-[#25D366] transition-colors" target="_blank" rel="noreferrer">WhatsApp: {data.whatsapp}</a></li>}
            {data.email && <li className="flex items-center gap-3" data-testid="cms-contact-email"><Mail className="w-4.5 h-4.5 text-brand-500" strokeWidth={1.75} /><a href={`mailto:${data.email}`} className="hover:text-brand-500 transition-colors break-all">{data.email}</a></li>}
            {data.finance_email && <li className="flex items-center gap-3"><Mail className="w-4.5 h-4.5 text-ink-400" strokeWidth={1.75} /><a href={`mailto:${data.finance_email}`} className="hover:text-brand-500 transition-colors break-all">{data.finance_email}</a></li>}
            {data.opening_hours && <li className="flex items-center gap-3"><Clock className="w-4.5 h-4.5 text-brand-500" strokeWidth={1.75} /><span>{data.opening_hours}</span></li>}
          </ul>
        </div>
        {data.extra_text && (
          <div className="card-dark p-8">
            <span className="eyebrow text-brand-300">Goed om te weten</span>
            <p className="mt-3 text-[15px] text-white/85 leading-relaxed whitespace-pre-line">{data.extra_text}</p>
          </div>
        )}
      </div>
    </section>
  );
}

// ===================== FORM INTRO =====================
export function FormIntroBlock({ data }) {
  if (!data || (!data.title && !data.text)) return null;
  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="cms-block-form-intro">
      <div className="container-narrow text-center">
        {data.title && <h2 className="font-chivo font-black text-3xl text-brand-900 tracking-tight">{data.title}</h2>}
        {data.text && <p className="text-ink-500 mt-3 text-[15px] leading-relaxed max-w-2xl mx-auto">{data.text}</p>}
        {data.button_text && data.button_link && (
          <Link to={data.button_link} className="btn-primary mt-7" data-testid="cms-form-intro-cta">
            {data.button_text} <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
          </Link>
        )}
      </div>
    </section>
  );
}

// ===================== FOOTER COLUMNS (CMS preview) =====================
export function FooterColumnsBlock({ data }) {
  if (!data) return null;
  const cols = data.columns || [];
  return (
    <section className="py-16 bg-brand-900 text-white" data-testid="cms-block-footer-columns">
      <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-8">
        {data.tagline && <p className="md:col-span-3 text-sm text-white/70">{data.tagline}</p>}
        {cols.map((c, i) => (
          <div key={i}>
            {c.title && <h3 className="font-chivo font-black text-[11px] uppercase tracking-[0.22em] text-brand-300 mb-4">{c.title}</h3>}
            {c.links && (
              <ul className="space-y-2 text-sm text-white/80">
                {c.links.map((l, j) => (
                  <li key={j}><Link to={l.url} className="hover:text-white transition-colors">{l.label}</Link></li>
                ))}
              </ul>
            )}
            {(c.phone || c.email || c.address || c.opening_hours) && (
              <ul className="space-y-2 text-sm text-white/80 mt-2">
                {c.phone && <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-brand-300" />{c.phone}</li>}
                {c.email && <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-brand-300" />{c.email}</li>}
                {c.address && <li className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 mt-1 text-brand-300" />{c.address}</li>}
                {c.opening_hours && <li className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-brand-300" />{c.opening_hours}</li>}
              </ul>
            )}
          </div>
        ))}
        {data.legal_text && <p className="md:col-span-3 text-xs text-white/60 mt-4 pt-4 border-t border-white/10">{data.legal_text}</p>}
      </div>
    </section>
  );
}

// ===================== NAV LINKS (CMS preview) =====================
export function NavigationLinksBlock({ data }) {
  if (!data) return null;
  return (
    <section className="py-6 bg-white border-y border-slate-200" data-testid="cms-block-navigation-preview">
      <div className="container-x flex flex-wrap items-center gap-4 text-sm">
        <span className="eyebrow">Navigation preview:</span>
        {(data.menu_links || []).map((l, i) => (
          <Link key={i} to={l.url} className="text-brand-900 hover:text-brand-500 font-medium transition-colors">{l.label}</Link>
        ))}
        {data.cta_button_label && data.cta_button_url && (
          <Link to={data.cta_button_url} className="ml-auto btn-primary !py-2 !px-4 !text-xs">{data.cta_button_label}</Link>
        )}
      </div>
    </section>
  );
}

// ===================== BENEFITS LIST =====================
export function BenefitsListBlock({ data }) {
  if (!data) return null;
  const items = data.items || [];
  return (
    <section className="py-24 bg-white" data-testid="cms-block-benefits-list">
      <div className="container-x">
        {data.title && (
          <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight max-w-3xl">{data.title}</h2>
        )}
        {data.subtitle && <p className="text-ink-500 mt-3 max-w-2xl text-[15px] leading-relaxed">{data.subtitle}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {items.map((it, i) => (
            <div key={i} className="card-premium p-7 group" data-testid={`cms-benefit-${i}`}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-gradient text-white shadow-brand-soft group-hover:shadow-brand-glow transition-shadow">
                <Check className="w-5 h-5" strokeWidth={2.25} />
              </div>
              <h3 className="font-chivo font-bold text-lg text-brand-900 mt-5">{it.title}</h3>
              {it.description && <p className="text-[14.5px] text-ink-500 mt-2.5 leading-relaxed">{it.description}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===================== PROCESS STEPS =====================
export function ProcessStepsBlock({ data }) {
  if (!data) return null;
  const steps = data.steps || [];
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="cms-block-process-steps">
      <div className="container-x">
        {data.title && (
          <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight max-w-3xl">{data.title}</h2>
        )}
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 mt-12 relative">
          {steps.map((s, i) => (
            <li key={i} className="card-premium p-6 relative" data-testid={`cms-step-${i}`}>
              <div className="font-chivo font-black text-4xl text-brand-500/15 leading-none font-mono-num">
                {String(s.step || i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-chivo font-bold text-base text-brand-900 mt-3">{s.title}</h3>
              {s.description && <p className="text-sm text-ink-500 mt-2 leading-relaxed">{s.description}</p>}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ===================== DOCUMENT DOWNLOADS =====================
const CATEGORY_LABELS = {
  "checklists": "Checklists",
  "machtigingen": "Machtigingen",
  "paklijsten": "Paklijsten",
  "boekingsformulieren": "Boekingsformulieren",
  "douane": "Douane / documentatie",
  "verhuizing": "Verhuizing",
  "export": "Export",
  "algemene-voorwaarden": "Algemene voorwaarden",
};

export function DocumentDownloadsBlock({ data }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/website/documents`)
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]));
  }, []);
  if (!data) return null;
  // Phase 18.4 helper — every backend-relative URL must be prefixed with
  // REACT_APP_BACKEND_URL. External URLs (https://…) pass through.
  const abs = (u) => {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
    return null;
  };
  return (
    <section className="py-24 bg-white" data-testid="cms-block-document-downloads">
      <div className="container-x">
        <span className="eyebrow">Downloads</span>
        {data.title && (
          <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-3">{data.title}</h2>
        )}
        {data.subtitle && <p className="text-ink-500 mt-3 max-w-2xl text-[15px] leading-relaxed">{data.subtitle}</p>}
        {docs === null ? (
          <div className="mt-10 text-sm text-ink-400" data-testid="cms-doc-downloads-loading">Documenten laden…</div>
        ) : docs.length === 0 ? (
          <div className="mt-10 card-premium px-6 py-12 text-center text-sm text-ink-500" data-testid="cms-doc-downloads-empty">
            {data.empty_text || "Er zijn momenteel geen documenten beschikbaar."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10" data-testid="cms-doc-downloads-grid">
            {docs.map((d) => {
              // Phase 18.4: prefer the stable backend-anchored download_url
              // emitted by the backend. Fall back to file_url for external
              // links. NEVER use the raw relative file_url on the frontend
              // origin — that was the original bug.
              const downloadUrl = abs(d.download_url || d.file_url);
              const viewUrl = d.previewable ? abs(d.view_url) : null;
              const catLabel = CATEGORY_LABELS[d.category] || d.category;
              return (
                <div key={d.id} className="card-premium p-6 flex flex-col group" data-testid={`cms-doc-card-${d.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-brand-500/10 group-hover:bg-brand-500 group-hover:text-white text-brand-500 flex items-center justify-center transition-colors flex-shrink-0">
                      <FileText className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-chivo font-bold text-brand-500">{catLabel}</div>
                      <h3 className="font-chivo font-bold text-base text-brand-900 mt-1 leading-snug">{d.title}</h3>
                    </div>
                  </div>
                  {d.description && <p className="text-[14px] text-ink-500 mt-4 leading-relaxed flex-1">{d.description}</p>}
                  <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                    {viewUrl && (
                      <a
                        href={viewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-brand-200 text-brand-900 hover:bg-brand-50 text-xs font-chivo font-bold uppercase tracking-wide transition-colors"
                        data-testid={`cms-doc-view-${d.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" strokeWidth={2} />
                        Bekijken
                      </a>
                    )}
                    {downloadUrl ? (
                      <a
                        href={downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        download={d.file_name || undefined}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-900 hover:bg-brand-500 text-white text-xs font-chivo font-bold uppercase tracking-wide transition-colors"
                        data-testid={`cms-doc-download-${d.id}`}
                      >
                        <Download className="w-3.5 h-3.5" strokeWidth={2} />
                        Download {(d.file_type || "PDF").toUpperCase()}
                      </a>
                    ) : (
                      <span className="text-xs text-ink-400" data-testid={`cms-doc-missing-${d.id}`}>
                        Link niet beschikbaar
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

// ===================== DANGEROUS GOODS =====================
export function DangerousGoodsInfoBlock({ data }) {
  if (!data) return null;
  const cats = data.categories || [];
  const splitParas = (txt) => String(txt || "").split(/\n\n+/);
  return (
    <section className="py-24 bg-white border-t border-slate-200" data-testid="cms-block-dangerous-goods">
      <div className="container-x">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[11px] tracking-[0.18em] uppercase font-chivo font-bold">
          <ShieldAlert className="w-3.5 h-3.5" /> Belangrijke informatie
        </div>
        {data.title && (
          <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-4 max-w-3xl">
            {data.title}
          </h2>
        )}
        {data.intro && (
          <div className="mt-5 max-w-3xl text-ink-500 text-[15px] leading-relaxed space-y-3">
            {splitParas(data.intro).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
        {data.warning && (
          <div className="mt-7 rounded-xl bg-red-50 border-l-4 border-red-600 text-red-800 px-5 py-4 text-[14px] font-medium" data-testid="cms-dg-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4.5 h-4.5 mt-0.5 flex-shrink-0" />
              <span>{data.warning}</span>
            </div>
          </div>
        )}

        {cats.length > 0 && (
          <>
            {data.categories_title && (
              <h3 className="font-chivo font-bold text-xl text-brand-900 mt-14">{data.categories_title}</h3>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="cms-dg-categories">
              {cats.map((c, i) => (
                <div key={i} className="card-premium p-5" data-testid={`cms-dg-category-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-50 text-red-700 flex items-center justify-center font-chivo font-black text-sm">
                      {i + 1}
                    </span>
                    <h4 className="font-chivo font-bold text-sm text-brand-900 leading-snug pt-0.5">{c.title}</h4>
                  </div>
                  {Array.isArray(c.examples) && c.examples.length > 0 && (
                    <ul className="mt-3 text-xs text-ink-500 space-y-1.5 pl-1">
                      {c.examples.map((ex, j) => (
                        <li key={j} className="flex items-start gap-1.5">
                          <span className="text-red-600 mt-0.5">•</span>
                          <span>{ex}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {(data.dg_title || data.dg_text) && (
          <div className="mt-14 card-dark p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-12 w-72 h-72 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" />
            <div className="relative">
              {data.dg_title && <h3 className="font-chivo font-black text-2xl sm:text-3xl">{data.dg_title}</h3>}
              {data.dg_text && (
                <div className="mt-4 text-[15px] text-white/85 leading-relaxed space-y-3 max-w-3xl">
                  {splitParas(data.dg_text).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
            </div>
          </div>
        )}

        {data.disclaimer && (
          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 text-xs text-ink-500 leading-relaxed space-y-2" data-testid="cms-dg-disclaimer">
            <div className="text-[10px] uppercase tracking-[0.18em] font-chivo font-bold text-brand-900">Disclaimer</div>
            {splitParas(data.disclaimer).map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}

        {(data.cta_title || data.cta_button_label) && (
          <div className="mt-10 card-premium p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-l-4 border-l-brand-500">
            <div>
              {data.cta_title && <div className="font-chivo font-bold text-base text-brand-900">{data.cta_title}</div>}
              {data.cta_text && <div className="text-sm text-ink-500 mt-1 max-w-2xl">{data.cta_text}</div>}
            </div>
            {data.cta_button_label && (
              <Link
                to={data.cta_button_link || "/contact"}
                className="btn-dark flex-shrink-0"
                data-testid="cms-dg-cta"
              >
                {data.cta_button_label} <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ===================== BlockRenderer entry =====================
export default function BlockRenderer({ blocks, tariffs = [], slug }) {
  if (!blocks) return null;
  const visible = blocks.filter((b) => b.active !== false).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const faqBlocks = visible.filter((b) => b.type === "faq");
  let pricingInjected = false;

  // -----------------------------------------------------------------
  // /online-betalen compact pairing — text_sections between the stripe
  // button and the final "Belangrijk voor vertrek" are grouped into a
  // 2-column responsive grid so the page reads as a dashboard instead
  // of a long scroll. Stripe block always stays directly after the
  // hero. The last text_section ("Belangrijk voor vertrek") is always
  // rendered full-width.
  // -----------------------------------------------------------------
  let pairingPlan = null;
  if (slug === "online-betalen") {
    const types = visible.map((b) => b.type);
    const heroIdx = types.indexOf("hero");
    const stripeIdx = types.indexOf("stripe_buy_button");
    let lastIdx = -1;
    for (let i = visible.length - 1; i > stripeIdx; i--) {
      if (visible[i].type === "text_section") { lastIdx = i; break; }
    }
    if (stripeIdx > -1 && lastIdx > stripeIdx + 1) {
      const middleIds = new Set();
      for (let i = stripeIdx + 1; i < lastIdx; i++) {
        const b = visible[i];
        if (b.type !== "text_section" && b.type !== "cta_banner") continue;
        // Skip the "Snel en veilig afrekenen" text_section because the
        // same content is rendered as a compact instruction card right
        // next to the Stripe button on the stripe_buy_button block.
        const title = String(b.data?.title || "").toLowerCase().trim();
        if (title.startsWith("snel en veilig afrekenen") || title.startsWith("quick and secure")) continue;
        middleIds.add(b.id);
      }
      pairingPlan = { heroIdx, stripeIdx, lastIdx, middleIds };
    }
  }

  if (pairingPlan) {
    const { stripeIdx, lastIdx, middleIds } = pairingPlan;
    const elements = [];
    let i = 0;
    while (i < visible.length) {
      const b = visible[i];
      // Skip the "Snel en veilig afrekenen" duplicate completely — it is
      // rendered as the instruction card next to the Stripe button.
      if (i > stripeIdx && i < lastIdx && b.type === "text_section") {
        const title = String(b.data?.title || "").toLowerCase().trim();
        if (title.startsWith("snel en veilig afrekenen") || title.startsWith("quick and secure")) {
          i += 1;
          continue;
        }
      }
      if (i > stripeIdx && i < lastIdx && middleIds.has(b.id)) {
        // Collect a contiguous run of paired middle blocks and render
        // them in a responsive 2-column grid section.
        const group = [];
        while (i < lastIdx && middleIds.has(visible[i].id)) {
          group.push(visible[i]);
          i += 1;
        }
        elements.push(
          <section key={`pair-${group[0].id}`} className="py-12 bg-white" data-testid="online-betalen-info-grid">
            <div className="container-x">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {group.map((gb) => {
                  if (gb.type === "text_section") {
                    return <TextSectionCard key={gb.id} data={gb.data} />;
                  }
                  if (gb.type === "cta_banner") {
                    return (
                      <div key={gb.id} className="rounded-2xl bg-brand-gradient text-white p-6 sm:p-7 shadow-brand-deep overflow-hidden relative h-full flex flex-col" data-testid="cms-block-cta-banner">
                        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-500/30 blur-3xl pointer-events-none" />
                        <div className="relative flex flex-col h-full">
                          {gb.data?.title && (
                            <h3 className="font-chivo font-black text-xl sm:text-2xl tracking-tight">{gb.data.title}</h3>
                          )}
                          {(gb.data?.text || gb.data?.subtitle) && (
                            <p className="mt-2 text-white/85 text-[14.5px] leading-relaxed">{gb.data.text || gb.data.subtitle}</p>
                          )}
                          {(gb.data?.button_text || gb.data?.button_label) && (gb.data?.button_link) && (
                            <div className="mt-auto pt-5">
                              <Link to={gb.data.button_link} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-brand-900 hover:bg-brand-50 font-chivo font-bold text-[13px] tracking-wide uppercase shadow-brand-card transition-all active:scale-[0.98]" data-testid="cms-cta-button">
                                {gb.data.button_text || gb.data.button_label} <ArrowRight className="w-4 h-4" strokeWidth={2.25} />
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </section>
        );
        continue;
      }
      elements.push(_renderBlock(b, tariffs, pricingInjected));
      if (b.type === "service_cards" && tariffs.length > 0) pricingInjected = true;
      i += 1;
    }
    return (
      <>
        {elements}
        {!pricingInjected && tariffs.length > 0 && <PricingTableBlock tariffs={tariffs} />}
        <FaqGroup blocks={faqBlocks} />
      </>
    );
  }

  return (
    <>
      {visible.map((b) => {
        const out = _renderBlock(b, tariffs, pricingInjected);
        if (b.type === "service_cards" && tariffs.length > 0) pricingInjected = true;
        if (b.type === "pricing" && tariffs.length > 0) pricingInjected = true;
        return out;
      })}
      {!pricingInjected && tariffs.length > 0 && <PricingTableBlock tariffs={tariffs} />}
      <FaqGroup blocks={faqBlocks} />
    </>
  );
}

// Internal helper — renders a single block. Used both by the normal
// renderer pass and by the /online-betalen compact pairing pass.
function _renderBlock(b, tariffs, pricingInjected) {
  if (b.type === "hero") return <HeroBlock key={b.id} data={b.data} />;
  if (b.type === "text_section") return <TextSection key={b.id} data={b.data} />;
  if (b.type === "service_cards") {
    const pricing = !pricingInjected && tariffs.length > 0
      ? <PricingTableBlock key={`pricing-${b.id}`} tariffs={tariffs} /> : null;
    return (
      <React.Fragment key={b.id}>
        <ServiceCardsBlock data={b.data} />
        {pricing}
      </React.Fragment>
    );
  }
  if (b.type === "cta_banner") return <CtaBannerBlock key={b.id} data={b.data} />;
  if (b.type === "location_cards") return <LocationCardsBlock key={b.id} data={b.data} />;
  if (b.type === "product_highlights") return <ProductHighlightsBlock key={b.id} data={b.data} />;
  if (b.type === "contact_block") return <ContactBlock key={b.id} data={b.data} />;
  if (b.type === "form_intro") return <FormIntroBlock key={b.id} data={b.data} />;
  // footer_columns and navigation_links are layout chrome (rendered by
  // Footer.jsx / Header.jsx via the dedicated "footer" / "header" CMS
  // pages). If they end up in any other page's blocks we deliberately
  // do NOT render them inline — otherwise the public site would show a
  // ghost footer block above the real footer.
  if (b.type === "footer_columns") return null;
  if (b.type === "navigation_links") return null;
  if (b.type === "benefits_list") return <BenefitsListBlock key={b.id} data={b.data} />;
  if (b.type === "process_steps") return <ProcessStepsBlock key={b.id} data={b.data} />;
  if (b.type === "document_downloads") return <DocumentDownloadsBlock key={b.id} data={b.data} />;
  if (b.type === "dangerous_goods_info") return <DangerousGoodsInfoBlock key={b.id} data={b.data} />;
  if (b.type === "agent_application_form") return (
    <section key={b.id} id={b.data?.anchor_id || "agent-form"} className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="cms-block-agent-application-form">
      <div className="container-narrow">
        {b.data?.title && (<h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight">{b.data.title}</h2>)}
        {b.data?.subtitle && <p className="text-ink-500 mt-3 text-[15px] leading-relaxed max-w-2xl">{b.data.subtitle}</p>}
        <div className="mt-10 card-premium p-8"><AgentApplicationForm /></div>
      </div>
    </section>
  );
  if (b.type === "payment_reference_form") return (
    <section key={b.id} className="py-20 bg-white" data-testid="cms-block-payment-reference-form">
      <div className="container-narrow">
        {b.data?.title && (<h2 className="font-chivo font-black text-2xl sm:text-3xl text-brand-900 tracking-tight">{b.data.title}</h2>)}
        {b.data?.subtitle && (
          <div className="rounded-xl border-l-4 border-amber-500 bg-amber-50 px-4 py-3 mt-4 text-sm text-amber-900 font-medium">
            {b.data.subtitle}
          </div>
        )}
        <div className="mt-7 card-premium p-8"><PaymentReferenceForm /></div>
      </div>
    </section>
  );
  if (b.type === "stripe_buy_button") return (
    <section key={b.id} className="py-12 bg-gradient-to-b from-white via-slate-50 to-white" data-testid="cms-block-stripe-buy-button">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-stretch">
          {/* Stripe card — prominent, 3/5 width on desktop */}
          <div className="md:col-span-3">
            <div className="card-premium p-6 sm:p-8 h-full text-center" data-testid="stripe-checkout-wrapper">
              {b.data?.title && (<h2 className="font-chivo font-black text-2xl sm:text-3xl text-brand-900 tracking-tight">{b.data.title}</h2>)}
              {b.data?.description && (<p className="text-[14.5px] text-ink-500 mt-3 max-w-2xl mx-auto leading-relaxed">{b.data.description}</p>)}
              <div className="mt-6 flex items-center justify-center gap-2 text-[11px] tracking-[0.18em] uppercase font-chivo font-bold text-brand-500">
                <ShieldAlert className="w-3.5 h-3.5" />
                SSL Secured · Powered by Stripe
              </div>
              <div className="mt-5 flex flex-col items-center gap-3">
                <StripeBuyButtonBlock
                  buyButtonId={b.data?.buy_button_id}
                  publishableKey={b.data?.publishable_key}
                  paymentLinkUrl={b.data?.payment_link_url}
                />
              </div>
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-center gap-3 text-[11px] text-ink-400 font-mono-num">
                <span>VISA</span>
                <span className="text-slate-300">·</span>
                <span>Mastercard</span>
                <span className="text-slate-300">·</span>
                <span>iDEAL</span>
                <span className="text-slate-300">·</span>
                <span>Apple Pay</span>
              </div>
            </div>
          </div>
          {/* Compact instructions card — 2/5 width on desktop */}
          <div className="md:col-span-2">
            <div className="card-dark p-6 sm:p-7 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-12 w-48 h-48 rounded-full bg-brand-500/30 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="text-[11px] tracking-[0.18em] uppercase font-chivo font-bold text-brand-300 mb-3">Snel en veilig afrekenen</div>
                <ul className="space-y-3 text-[14px] text-white/90">
                  <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-brand-200 flex-shrink-0" strokeWidth={2.5} />Controleer uw factuurgegevens vóór betaling.</li>
                  <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-brand-200 flex-shrink-0" strokeWidth={2.5} />Rond uw betaling veilig af via Stripe.</li>
                  <li className="flex items-start gap-2.5"><Check className="w-4 h-4 mt-0.5 text-brand-200 flex-shrink-0" strokeWidth={2.5} />Bewaar uw betalingsbevestiging.</li>
                </ul>
                <div className="mt-5 pt-5 border-t border-white/15 text-[13px] text-white/85">
                  Vragen?{" "}
                  <a href="mailto:finance@5999cargo.nl" className="text-brand-200 underline underline-offset-4 hover:text-white" data-testid="online-betalen-finance-email">
                    finance@5999cargo.nl
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
  if (b.type === "pricing") {
    if (tariffs && tariffs.length > 0) {
      return (
        <div key={b.id} id={b.data?.anchor_id || "tarieven"}>
          {b.data?.title && (
            <div className="bg-white pt-16 px-4">
              <div className="container-narrow">
                <span className="eyebrow">Tarieven</span>
                <h2 className="font-chivo font-black text-3xl sm:text-4xl text-brand-900 tracking-tight mt-3">{b.data.title}</h2>
                {b.data?.subtitle && <p className="text-[15px] text-ink-500 mt-3 max-w-2xl leading-relaxed">{b.data.subtitle}</p>}
              </div>
            </div>
          )}
          <PricingTableBlock tariffs={tariffs} />
        </div>
      );
    }
    return null;
  }
  if (b.type === "faq") return null;
  return null;
}
